import { FilterQuery } from '@models/filter-query.model';
import { UserActivity } from '@models/user-activity.model';
import { AfterTestSuiteConfig } from '@modules/test-development/models/after-test-suite-config.model';
import { ApplicationType } from '@testsigmainc/constants';
import {
  alias,
  custom,
  deserialize,
  getDefaultModelSchema,
  object,
  optional,
  serializable,
  SKIP,
} from 'serializr';
import { Base } from '../../../models/base.model';
import { PageObject } from '../../../models/page-object.model';
import { DynamicPageableQuery } from '../components/suites/dynamic-suite/type/dynamic-pageable-query.type';
import { TestCaseFilter } from './test-case-filter.model';
import { TestSuiteResult } from './test-suite-result.model';

export class TestSuite extends Base implements PageObject {
  constructor() {
    super();
    getDefaultModelSchema(TestSuite).props['preRequisiteSuite'] = optional(
      object(TestSuite),
    );
  }
  @serializable
  public name: string;
  @serializable
  public description: string;
  @serializable
  public appVersionId: number;
  @serializable
  public preRequisite: number;
  @serializable(alias('manual'))
  public isManual: boolean;
  @serializable(optional())
  public preRequisiteSuite: TestSuite;
  @serializable
  public createdById: number;
  @serializable
  public updatedById: number;
  @serializable(
    custom(
      () => SKIP,
      (v) => {
        if (v) return new TestSuiteResult().deserialize(v);
      },
    ),
  )
  public lastRun: TestSuiteResult;
  @serializable(
    custom(
      (v) => v,
      () => SKIP,
    ),
  )
  public testCaseIds: number[];
  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public tags: string[];
  @serializable
  public totalTestCasesCount;

  @serializable(
    custom(
      (v) => {
        if (v) return JSON.stringify(v);
        return SKIP;
      },
      (v) => {
        const filter: FilterQuery[] = [];
        v = Array.isArray(v) ? v : JSON.parse(v);
        if (v) {
          v.forEach((query) => {
            filter.push(new FilterQuery().deserialize(query));
          });
          return filter;
        }
        return SKIP;
      },
    ),
  )
  public dynamicFilterQuery: FilterQuery[];

  @serializable(
    custom(
      () => SKIP,
      (v) => new TestCaseFilter().deserialize(v),
    ),
  )
  public dynamicFilter: TestCaseFilter;

  @serializable
  public dynamicFilterId: number;

  @serializable(
    custom(
      (v) => {
        if (v) return JSON.stringify(v);
        return SKIP;
      },
      (v) => {
        v = typeof v == 'string' ? JSON.parse(v) : v;
        if (v) {
          return v;
        }
        return SKIP;
      },
    ),
  )
  public dynamicPageableQuery: DynamicPageableQuery;

  @serializable(
    custom(
      (v) => {
        if (v) return JSON.stringify(v);
        return SKIP;
      },
      (v) => {
        const filter: FilterQuery[] = [];
        v = Array.isArray(v) ? v : JSON.parse(v);
        if (v) {
          v.forEach((query) => {
            filter.push(new FilterQuery().deserialize(query));
          });
          return filter;
        }
        return SKIP;
      },
    ),
  )
  public afterSuiteDynamicFilterQuery: FilterQuery[];

  @serializable(
    custom(
      () => SKIP,
      (v) => new TestCaseFilter().deserialize(v),
    ),
  )
  public afterSuiteDynamicFilter: TestCaseFilter;

  @serializable
  public afterSuiteDynamicFilterId: number;

  @serializable(
    custom(
      (v) => {
        if (v) return JSON.stringify(v);
        return SKIP;
      },
      (v) => {
        v = typeof v == 'string' ? JSON.parse(v) : v;
        if (v) {
          return v;
        }
        return SKIP;
      },
    ),
  )
  public afterSuiteDynamicPageableQuery: DynamicPageableQuery;

  @serializable(
    custom(
      (v) => v,
      () => SKIP,
    ),
  )
  public afterTestSuiteCaseIds: number[];

  @serializable(optional(object(AfterTestSuiteConfig)))
  public afterTestSuiteConfig: AfterTestSuiteConfig;

  @serializable
  public hasAfterSuite: boolean;

  public createdBy?: UserActivity;
  public updatedBy?: UserActivity;

  public afterTestSuiteId: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public filteredTestCaseCount: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public filteredAfterSuiteTestCaseCount: number;

  @serializable(
    custom(
      () => SKIP,
      (v) => v,
    ),
  )
  public appType: ApplicationType;

  get suiteTypeTranslation(): string {
    if (this?.isDynamicSuite) return 'common.dynamic';
    return 'common.static';
  }

  get afterSuiteDynamicFilterName(): string {
    if (this?.isDynamicSuite) {
      return this.afterSuiteDynamicFilter?.name;
    }
    return '';
  }

  get dynamicFilterName(): string {
    if (this?.isDynamicSuite) {
      return this.dynamicFilter?.name;
    }
    return '';
  }

  get isDynamicSuite(): boolean {
    return this.dynamicFilterQuery?.length > 0 || !!this.dynamicFilter?.id;
  }

  deserialize(input: any): this {
    return Object.assign(this, deserialize(TestSuite, input));
  }
}
